<script setup lang="ts">
//#region imports
import { useI18n } from 'vue-i18n';
import type {
  AdminPermissionKey,
  AdminPermissionValue,
  PermissionSwitchValue,
} from '@/types/teams';
//#endregion

//#region types
interface PermissionDependency {
  name: AdminPermissionKey;
  selected: boolean;
}

interface Props {
  permission: AdminPermissionKey;
  value?: PermissionSwitchValue;
  dependencies: PermissionDependency[];
  disabled: boolean;
  inherit?: boolean;
  computedValue?: AdminPermissionValue;
  inheritDisabled?: boolean;
  showInherit?: boolean;
  switchClass?: string;
  tooltipShowAfter?: number;
}

interface Emits {
  change: [permission: AdminPermissionKey, value: PermissionSwitchValue];
  inheritChange: [permission: AdminPermissionKey, value: boolean];
}
//#endregion

//#region define
const props = withDefaults(defineProps<Props>(), {
  inheritDisabled: false,
  showInherit: false,
  switchClass: '',
});
const emit = defineEmits<Emits>();
//#endregion

//#region composables
const { t } = useI18n();
//#endregion
</script>

<template>
  <div class="permission-switch-control">
    <span class="permission-switch-control__label">{{ t(`permissions.list.${props.permission}`) }}</span>
    <ElTooltip
      placement="right"
      :disabled="!props.dependencies.length"
      :show-after="props.tooltipShowAfter"
    >
      <div class="t:flex">
        <div
          v-if="props.showInherit"
          class="permission-switch-control__inherit-check-wrapper"
        >
          <UiCheckbox
            :type="props.computedValue === 'deny' ? 'negative' : ''"
            :disabled="props.inheritDisabled || props.disabled"
            :model-value="props.inherit"
            @change="emit('inheritChange', props.permission, $event)"
          />
        </div>
        <UiSwitch
          v-if="!props.showInherit || !props.inherit"
          :class="props.switchClass"
          :disabled="props.disabled || props.inheritDisabled"
          active-value="allow"
          inactive-value="deny"
          :model-value="props.value"
          @change="emit('change', props.permission, $event)"
        />
        <div
          v-else
          class="permission-switch-control__access-status"
        >
          <UiStatusText
            :type="props.computedValue === 'deny' ? 'negative' : 'positive'"
            :text="props.computedValue ? t(`permissions.${props.computedValue}`) : ''"
          />
        </div>
      </div>
      <template v-slot:content>
        <div>
          <h3 class="t:mb-5">
            {{ t('permissions.depends') }}
          </h3>
          <div
            v-for="dependency in props.dependencies"
            :key="dependency.name"
            :class="{ 't:line-through': dependency.selected }"
          >
            {{ t(`permissions.list.${dependency.name}`) }}
          </div>
        </div>
      </template>
    </ElTooltip>
  </div>
</template>

<style lang="scss">
.permission-switch-control {
  &__label {
    max-width: 205px;
  }
  &__inherit-check-wrapper {
    display: flex;
    justify-content: center;
    min-width: 50px;
    max-width: 65px;
  }

  &__access-status {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 72px;
  }
}
</style>
